/**
 * The two-line contract every screen in the OAuth authorization flow shares.
 *
 * Better Auth's provider hands the browser a SIGNED query string when it needs a human —
 * `/sign-in?<params+sig>` to authenticate, `/consent?<params+sig>` to approve. Whatever the
 * screen does next, it must hand that string back untouched as `oauth_query`; the provider
 * verifies the signature, restores the pending authorization, and answers with the next URL.
 *
 * Both halves are here because both are easy to get subtly wrong in the same way — losing or
 * re-encoding the string, or reading the answer from the wrong field — and the symptom in
 * every case is identical and silent: the user appears signed in, and the connector they
 * were approving never completes.
 */

/**
 * The signed query exactly as the provider wrote it.
 *
 * `.slice(1)` on the raw search string, deliberately NOT `URLSearchParams` round-tripped:
 * the signature covers a canonical serialisation, and re-encoding is free to alter escaping
 * (`%20` vs `+`, ordering) and invalidate it. The string is opaque to us — carry it, do not
 * process it.
 */
export function oauthQueryFromSearch(search: string): string | undefined {
  const q = search.startsWith('?') ? search.slice(1) : search;
  return q.length > 0 ? q : undefined;
}

/**
 * Where the provider says to go next.
 *
 * Two shapes, because two endpoints answer: the post-sign-in hook resumes authorization and
 * returns `{ redirect, url }`, while `/oauth2/consent` documents `{ redirect_uri }`. Reading
 * only one of them works right up until the user reaches the other screen.
 */
export function redirectUrlFrom(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as Record<string, unknown>;
  const candidate = d.url ?? d.redirect_uri ?? d.redirectURI;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : undefined;
}
